/**
 * Determine the greatest common divisor between two values. This alllows us to
 * reduce values proportionally while maintaining the same ratio.
 *
 * @param a int
 * @param b int
 *
 * @return int
 */
export const gcd = (a, b)=> {
  if (!b) {
    return a;
  }

  return gcd(b, a % b);
};

/**
 * Limit the denominator of a fraction such that the denominator remains in
 * the bounds of the passed in limit.
 *
 * This code was ported over from Python 2.7's fraction module:
 *    https://github.com/python/cpython/blob/2.7/Lib/fractions.py#L206-L259
 *
 * @param limit int Limit that the denominator should not exceed
 * @param num int Numerator value
 * @param denom int Denominator value
 *
 * @return object
 */
export const limitDenominator = (limit, num, denom)=> {
  if (denom <= limit) {
    return {
      numerator: num,
      denominator: denom
    };
  }

  var p0 = 0;
  var q0 = 1;
  var p1 = 1;
  var q1 = 0;
  var n = num;
  var d = denom;

  const go = true;
  while (go) {
    const a = Math.floor(n / d);
    const q2 = q0 + (a * q1);

    if (q2 > limit) {
      break;
    }

    const cachedN = n;
    const cachedP0 = p0;

    p0 = p1;
    q0 = q1;
    p1 = cachedP0 + (a * p1);
    q1 = q2;
    n = d;
    d = cachedN - (a * d);
  }

  const k = Math.floor((limit - q0) / q1);

  const upperBound = {
    numerator: p0 + (k * p1),
    denominator: q0 + (k * q1)
  };

  const lowerBound = {
    numerator: p1,
    denominator: q1
  };

  const calculatedLowerBound = Math.abs(
    (lowerBound['numerator'] / lowerBound['denominator']) - (num / denom)
  );

  const calculatedUpperBound = Math.abs(
    (upperBound['numerator'] / upperBound['denominator']) - (num / denom)
  );

  if (calculatedLowerBound <= calculatedUpperBound) {
    return lowerBound;
  }

  return upperBound;
};

/**
 * Given a float value, return an object with a numerator and denominator such
 * that it has been reduced as far as it can.
 *
 * @param floatVal float
 * @param floatPrecision int
 *
 * @return object
 */
export const getReducedFractionFromFloat = (floatVal, floatPrecision=4)=> {
  const fixedVal = floatVal.toFixed(floatPrecision);
  const len = fixedVal.split('.')[1].length;

  var denominator = Math.pow(10, len);
  var numerator = parseFloat(fixedVal) * denominator;

  var divisor = gcd(numerator, denominator);

  numerator /= divisor;
  denominator /= divisor;

  return limitDenominator(100, numerator, denominator);
};
